import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Tooltip, Dropdown, Modal, Avatar, Button } from 'antd';
import { BellIcon } from 'lucide-react';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import { CalendarOutlined, BellOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface NotificationItem {
  notificationid: string;
  notificationtype: string;
  messagetext: string;
  notificationdate: Date;
  nominatorname?: string;
  awardname?: string;
  nominatorimage?: string;
  ecardimage?: string;
}

// Module-level — resets on every full page load, persists across navigations within the SPA
const modalShownForUsers = new Set<string>();

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  Appreciation: { bg: '#E6F7FF', text: '#1677FF' },
  Celebration:  { bg: '#F6FFED', text: '#52C41A' },
  Nomination:   { bg: '#FFF7E6', text: '#FA8C16' },
  Points:       { bg: '#F9F0FF', text: '#722ED1' },
};

export const NotificationIcon: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [welcomeModal, setWelcomeModal] = useState<NotificationItem[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  // DB-backed read set — survives localStorage clears and re-logins
  const readSetRef = useRef<Set<string>>(new Set());

  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const baseURL = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem('UserId') ?? '';

  const fetchToken = useCallback(async (): Promise<string> => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.idToken;
  }, [instance, accounts]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await fetchToken();

      // Fetch notifications, read IDs, and total points in parallel
      const [notifRes, readRes, pointsRes] = await Promise.all([
        fetch(`${baseURL}/api/UserNotification/GetAllUserNotifications/${userId}`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }),
        fetch(`${baseURL}/api/UserNotification/GetReadNotificationIds/${userId}`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }),
        fetch(`${baseURL}/api/UserNotification/GetUserTotalPoints/${userId}`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!notifRes.ok || !readRes.ok) return;

      const [data, readIds, points]: [NotificationItem[], string[], number] = await Promise.all([
        notifRes.json(),
        readRes.json(),
        pointsRes.ok ? pointsRes.json() : Promise.resolve(0),
      ]);

      setTotalPoints(points);

      readSetRef.current = new Set<string>(readIds.map(String));
      // Only show notifications from today onwards that the user hasn't read yet
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const unread = data.filter(
        (n) =>
          !readSetRef.current.has(String(n.notificationid)) &&
          new Date(n.notificationdate) >= todayStart
      );
      setNotifications(unread);
      setUnreadCount(unread.length);

      // Show centered modal once per page load (module-level flag resets on refresh/login)
      if (unread.length > 0 && !modalShownForUsers.has(userId)) {
        modalShownForUsers.add(userId);
        setWelcomeModal(unread);
        setModalVisible(true);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [baseURL, fetchToken, navigate, userId]);

  // Initial load
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Poll every 60s — detect new notifications and bump unread count
  useEffect(() => {
    const timer = setInterval(async () => {
      if (!userId) return;
      try {
        const token = await fetchToken();
        const res = await fetch(
          `${baseURL}/api/UserNotification/GetAllUserNotifications/${userId}`,
          { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const fresh: NotificationItem[] = await res.json();
        setNotifications((prev) => {
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const existingIds = new Set(prev.map((n) => String(n.notificationid)));
          // Only add notifications that are new, not already read, and from today onwards
          const newOnes = fresh.filter(
            (n) =>
              !existingIds.has(String(n.notificationid)) &&
              !readSetRef.current.has(String(n.notificationid)) &&
              new Date(n.notificationdate) >= todayStart
          );
          if (newOnes.length === 0) return prev;
          setUnreadCount((c) => c + newOnes.length);
          return [...prev, ...newOnes];
        });
      } catch {
        // polling errors are non-fatal
      }
    }, 10_000);
    return () => clearInterval(timer);
  }, [baseURL, fetchToken, userId]);

  const handleItemClick = useCallback(
    async (notificationId: string, notificationType: string) => {
      // Remove from list and reduce count immediately
      setNotifications((prev) => prev.filter((n) => String(n.notificationid) !== notificationId));
      if (!readSetRef.current.has(notificationId)) {
        readSetRef.current.add(notificationId);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        // Persist to DB — fire-and-forget
        try {
          const token = await fetchToken();
          await fetch(
            `${baseURL}/api/UserNotification/MarkNotificationRead/${userId}/${notificationId}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
          );
        } catch {
          // non-fatal
        }
      }
      navigate('/ViewNotification', { state: { notificationId, notificationType } });
    },
    [baseURL, fetchToken, navigate, userId]
  );

  const handleViewAll = useCallback(async () => {
    const unreadIds = notifications
      .map((n) => String(n.notificationid))
      .filter((id) => !readSetRef.current.has(id));

    notifications.forEach((n) => readSetRef.current.add(String(n.notificationid)));
    setNotifications([]);
    setUnreadCount(0);

    if (unreadIds.length > 0) {
      try {
        const token = await fetchToken();
        await fetch(
          `${baseURL}/api/UserNotification/MarkAllNotificationsRead/${userId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(unreadIds),
          }
        );
      } catch {
        // non-fatal
      }
    }

    navigate('/ViewNotification', { state: { notificationId: '0', notificationType: 'All' } });
  }, [baseURL, fetchToken, navigate, notifications, userId]);

  const handleModalItemClick = useCallback(
    async (notificationId: string, notificationType: string) => {
      setModalVisible(false);
      if (!readSetRef.current.has(notificationId)) {
        readSetRef.current.add(notificationId);
        setNotifications((prev) => prev.filter((n) => String(n.notificationid) !== notificationId));
        setUnreadCount((prev) => Math.max(0, prev - 1));
        try {
          const token = await fetchToken();
          await fetch(
            `${baseURL}/api/UserNotification/MarkNotificationRead/${userId}/${notificationId}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
          );
        } catch { /* non-fatal */ }
      }
      navigate('/ViewNotification', { state: { notificationId, notificationType } });
    },
    [baseURL, fetchToken, navigate, userId]
  );

  const handleModalViewAll = useCallback(async () => {
    setModalVisible(false);
    const unreadIds = welcomeModal
      .map((n) => String(n.notificationid))
      .filter((id) => !readSetRef.current.has(id));
    welcomeModal.forEach((n) => readSetRef.current.add(String(n.notificationid)));
    setNotifications([]);
    setUnreadCount(0);
    if (unreadIds.length > 0) {
      try {
        const token = await fetchToken();
        await fetch(`${baseURL}/api/UserNotification/MarkAllNotificationsRead/${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(unreadIds),
        });
      } catch { /* non-fatal */ }
    }
    navigate('/ViewNotification', { state: { notificationId: '0', notificationType: 'All' } });
  }, [baseURL, fetchToken, navigate, userId, welcomeModal]);

  const handleBellClick = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await fetchToken();
      await fetch(`${baseURL}/api/UserNotification/SaveLastNotificationData/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
    } catch {
      // non-fatal
    }
  }, [baseURL, fetchToken, userId]);

  const menu = (
    <div
      className="notification-menu"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E69CFF',
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
        minWidth: '320px',
        maxWidth: '380px',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          fontWeight: 600,
          fontSize: '14px',
          color: '#8629A4',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span
              style={{
                backgroundColor: '#FF4D4F',
                color: '#fff',
                borderRadius: '10px',
                padding: '1px 7px',
                fontSize: '12px',
              }}
            >
              {unreadCount} new
            </span>
          )}
        </div>
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#8629A4' }}>
          🏆 {totalPoints.toLocaleString()} pts
        </span>
      </div>

      {/* Items */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <div
            style={{
              padding: '20px 16px',
              textAlign: 'center',
              color: '#888888',
              fontSize: '13px',
            }}
          >
            You don't have any recent notifications
          </div>
        ) : (
          notifications.map((item) => {
            const isUnread = !readSetRef.current.has(String(item.notificationid));
            const typeStyle = TYPE_COLORS[item.notificationtype] ?? { bg: '#F5F5F5', text: '#666666' };

            return (
              <div
                key={item.notificationid}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  backgroundColor: isUnread ? '#FDF6FF' : '#FFFFFF',
                  transition: 'background-color 0.15s',
                }}
                onClick={() => handleItemClick(String(item.notificationid), item.notificationtype)}
              >
                {/* Type badge + unread dot */}
                <div style={{ marginBottom: '4px' }}>
                  <span
                    style={{
                      backgroundColor: typeStyle.bg,
                      color: typeStyle.text,
                      borderRadius: '4px',
                      padding: '1px 7px',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    {item.notificationtype}
                  </span>
                  {isUnread && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        backgroundColor: '#FF4D4F',
                        marginLeft: '8px',
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                </div>

                {/* Message */}
                <div
                  style={{
                    fontSize: '13px',
                    color: '#333333',
                    fontWeight: isUnread ? 600 : 400,
                    lineHeight: '1.4',
                  }}
                >
                  {item.messagetext}
                </div>

                {/* Date */}
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
                  <CalendarOutlined style={{ marginRight: '6px', color: '#888888', fontSize: '11px' }} />
                  <span style={{ fontSize: '11px', color: '#888888' }}>
                    {new Intl.DateTimeFormat('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                    }).format(new Date(item.notificationdate))}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 16px',
          textAlign: 'center',
          backgroundColor: '#F3E6FF',
          color: '#8629A4',
          cursor: 'pointer',
          fontWeight: 500,
          fontSize: '13px',
        }}
        onClick={handleViewAll}
      >
        View All Notifications
      </div>
    </div>
  );

  return (
    <>
    <Modal
      open={modalVisible}
      onCancel={() => setModalVisible(false)}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#8629A4' }}>
          <BellOutlined style={{ fontSize: '18px' }} />
          <span>You have {welcomeModal.length} new notification{welcomeModal.length > 1 ? 's' : ''}</span>
        </div>
      }
      footer={[
        <Button key="close" onClick={() => setModalVisible(false)}>
          Close
        </Button>,
        <Button key="viewall" type="primary" style={{ backgroundColor: '#8629A4', borderColor: '#8629A4' }} onClick={handleModalViewAll}>
          View All
        </Button>,
      ]}
      centered
      width={520}
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto', padding: '8px 0' } }}
    >
      {welcomeModal.map((item) => {
        const typeStyle = TYPE_COLORS[item.notificationtype] ?? { bg: '#F5F5F5', text: '#666' };
        return (
          <div
            key={item.notificationid}
            onClick={() => handleModalItemClick(String(item.notificationid), item.notificationtype)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px',
              padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#FDF6FF')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <Avatar
              src={item.nominatorimage ? `data:image/jpeg;base64,${item.nominatorimage}` : undefined}
              icon={!item.nominatorimage && <UserOutlined />}
              size={42}
              style={{ flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: '4px' }}>
                <span style={{
                  backgroundColor: typeStyle.bg, color: typeStyle.text,
                  borderRadius: '4px', padding: '1px 7px', fontSize: '11px', fontWeight: 600,
                }}>
                  {item.notificationtype}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#333', fontWeight: 500, lineHeight: '1.5' }}>
                {item.messagetext}
              </div>
              {item.awardname && (
                <div style={{ fontSize: '12px', color: '#8629A4', marginTop: '2px', fontWeight: 600 }}>
                  🏆 {item.awardname}
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CalendarOutlined />
                {new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(item.notificationdate))}
              </div>
            </div>
          </div>
        );
      })}
    </Modal>
    <Dropdown overlay={menu} trigger={['click']}>
      <Badge
        count={unreadCount}
        style={{
          backgroundColor: '#FF4D4F',
          color: '#FFFFFF',
          boxShadow: '0 0 0 1px #FFFFFF',
          fontSize: '10px',
          height: '16px',
          minWidth: '16px',
          lineHeight: '16px',
        }}
        offset={[-10, 5]}
      >
        <Tooltip
          title="Notifications"
          overlayInnerStyle={{ backgroundColor: '#8629A4', color: '#FFFFFF', fontSize: '12px' }}
          overlayStyle={{ boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-60%, -65%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              right: '24px',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              background: 'rgba(0, 0, 0, 0.25)',
              cursor: 'pointer',
            }}
          >
            <BellIcon
              style={{ width: '20px', height: '20px', color: '#FFFFFF' }}
              onClick={handleBellClick}
            />
          </div>
        </Tooltip>
      </Badge>
    </Dropdown>
    </>
  );
};

export default NotificationIcon;
